import { useEffect, useMemo, useState } from 'react'
import type { Partner } from '@shared/types'
import { KpiTile, StatusPill } from '../components/KpiTile.js'
import { askExtensionDate, extendQuoteValidity, fetchQuotes, type QuoteRow } from '../lib/api.js'
import { formatGBP } from '@shared/money'
import { daysUntil, formatUKDate } from '@shared/dates'

/**
 * Decision 209 (Mark, 2026-09-09): "Needs attention" leaves the cockpit for a
 * page of its own. The cockpit keeps a tile that counts it and opens this.
 *
 * Three lists, same thresholds as before (Organisation page):
 *   - sent but never viewed after `nudge_unviewed_days`
 *   - open and expiring inside `nudge_expiring_days`  → Extend (#208)
 *   - already EXPIRED (new here — the old panel only listed open quotes, so a
 *     quote that lapsed overnight vanished from it) → Extend & reinstate (#208)
 * Mine / Team scopes by owner, as the cockpit does.
 */

type Scope = 'mine' | 'team'
type Reason = { kind: 'unviewed' | 'expiring' | 'expired'; label: string }

export function NeedsAttention({ partner, onOpen }: { partner: Partner; onOpen: (quoteId: string) => void }) {
  const [openRows, setOpenRows] = useState<QuoteRow[]>([])
  const [expiredRows, setExpiredRows] = useState<QuoteRow[]>([])
  const [meId, setMeId] = useState<string | null>(null)
  const [nudgeDays, setNudgeDays] = useState<{ unviewed: number; expiring: number; openHorizon?: number }>({ unviewed: 5, expiring: 7 })
  const [scope, setScope] = useState<Scope>('team')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let live = true
    setLoading(true)
    Promise.all([fetchQuotes('open'), fetchQuotes('expired')])
      .then(([o, x]) => {
        if (!live) return
        setOpenRows(o.quotes); setExpiredRows(x.quotes); setMeId(o.meId ?? null); setError(null)
        if (o.nudgeDays) setNudgeDays(o.nudgeDays)
      })
      .catch((e: Error) => live && setError(e.message))
      .finally(() => live && setLoading(false))
    return () => { live = false }
  }, [partner.id, reloadKey])

  const mine = scope === 'mine' && meId != null
  const scoped = (rows: QuoteRow[]) => (mine ? rows.filter((q) => q.ownerId === meId) : rows)

  const items = useMemo(() => {
    const out: Array<{ quote: QuoteRow; reasons: Reason[] }> = []
    for (const q of scoped(openRows)) {
      const reasons: Reason[] = []
      if (q.status === 'sent' && q.sentAt) {
        const days = Math.floor((Date.now() - new Date(q.sentAt).getTime()) / 86_400_000)
        if (days >= nudgeDays.unviewed) reasons.push({ kind: 'unviewed', label: `not viewed — sent ${days}d ago` })
      }
      if (['sent', 'viewed'].includes(q.status) && q.validUntil) {
        const d = daysUntil(q.validUntil)
        if (d >= 0 && d <= nudgeDays.expiring) reasons.push({ kind: 'expiring', label: d === 0 ? 'expires today' : `expires in ${d}d` })
      }
      if (reasons.length) out.push({ quote: q, reasons })
    }
    // Expired: newest lapse first — the ones most worth reviving.
    const exp = scoped(expiredRows)
      .filter((q) => q.validUntil)
      .sort((a, b) => new Date(b.validUntil!).getTime() - new Date(a.validUntil!).getTime())
    for (const q of exp) {
      // Decision 251: only inside the open horizon — beyond it the quote is the archive's, not a chase.
      if (!q.priceLapsed) continue
      const d = -daysUntil(q.validUntil!)
      out.push({ quote: q, reasons: [{ kind: 'expired', label: d <= 0 ? 'price lapsed today — undecided' : `price lapsed ${d}d ago — undecided` }] })
    }
    return out
  }, [openRows, expiredRows, mine, meId, nudgeDays])   // eslint-disable-line react-hooks/exhaustive-deps -- scoped closes over mine/meId, both listed

  const counts = {
    unviewed: items.filter((i) => i.reasons.some((r) => r.kind === 'unviewed')).length,
    expiring: items.filter((i) => i.reasons.some((r) => r.kind === 'expiring')).length,
    expired: items.filter((i) => i.reasons.some((r) => r.kind === 'expired')).length,
  }

  const extend = (q: QuoteRow) => {
    const v = askExtensionDate(q.quoteNumber)
    if (!v) return
    extendQuoteValidity(q.id, v).then(() => setReloadKey((k) => k + 1)).catch((e: Error) => setError(e.message))
  }

  const section = (title: string, hint: string, kind: Reason['kind'], testId: string) => {
    const rows = items.filter((i) => i.reasons.some((r) => r.kind === kind))
    return (
      <div className="card" style={{ marginBottom: 16 }} data-testid={testId}>
        <div className="card__head">
          <div><h2>{title}</h2><span className="muted small">{hint}</span></div>
          <span className="muted small">{rows.length}</span>
        </div>
        <div className="table-wrap">
          <table>
            <tbody>
              {rows.length === 0 && <tr><td className="muted" colSpan={7}>Nothing here{mine ? ' of yours' : ''}.</td></tr>}
              {rows.map(({ quote: q, reasons }) => (
                <tr key={q.id} onClick={() => onOpen(q.id)} title="Open the quote" data-testid="attention-row">
                  <td><strong>{q.quoteNumber}</strong></td>
                  <td>{q.customer}</td>
                  <td><StatusPill status={q.status} lapsed={q.priceLapsed} /></td>
                  <td>
                    {reasons.map((r) => (
                      <span key={r.kind} className={`pill ${r.kind === 'unviewed' ? 'pill--info' : 'pill--warn'}`} style={{ marginRight: 6 }}>{r.label}</span>
                    ))}
                  </td>
                  <td className="muted">{q.ownerName ?? '—'}</td>
                  <td className="num">{formatGBP(q.valueSell)}</td>
                  <td className="muted small" onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    {kind === 'unviewed'
                      ? 'chase or resend'
                      : <button className="btn btn--ghost btn--sm" data-testid={kind === 'expired' ? 'attention-reinstate' : 'attention-extend'}
                          title={kind === 'expired' ? 'New valid-until date — the quote comes back to sent/viewed, its links work again, nothing emailed' : 'Push the valid-until date out — nothing emailed'}
                          onClick={() => extend(q)}>{kind === 'expired' ? 'Extend & reinstate' : 'Extend'}</button>}
                    {q.validUntil && <span className="muted small" style={{ marginLeft: 8 }}>until {formatUKDate(q.validUntil)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Needs attention</h1>
          <p>
            {partner.name} · quotes going quiet, about to lapse, or already lapsed. Thresholds are set on the Organisation page
            ({nudgeDays.unviewed}d unviewed · {nudgeDays.expiring}d to expiry).
          </p>
        </div>
        <div className="toolbar">
          <div className="toggle" role="group" aria-label="Quote scope">
            <button aria-pressed={scope === 'mine'} onClick={() => setScope('mine')} disabled={meId == null}
              title={meId == null ? 'Key sessions have no “me” — sign in via SSO or PIN' : 'Only quotes you own'}>Mine</button>
            <button aria-pressed={scope === 'team'} onClick={() => setScope('team')}>Team</button>
          </div>
        </div>
      </div>
      {error && <p className="pill pill--stop" style={{ marginBottom: 12 }}>{error}</p>}
      {loading && <p className="muted">Loading…</p>}
      {!loading && (
        <>
          <div className="tiles" data-testid="attention-tiles">
            <KpiTile accent label="Not viewed" value={String(counts.unviewed)} sub={`sent ${nudgeDays.unviewed}+ days ago, never opened`} />
            <KpiTile label="Expiring" value={String(counts.expiring)} sub={`inside ${nudgeDays.expiring} days`} />
            <KpiTile label="Expired" value={String(counts.expired)} sub="lapsed — extend & reinstate, or supersede" />
          </div>
          {section('Sent but not viewed', 'chase, or resend the link', 'unviewed', 'attention-unviewed')}
          {section('Expiring soon', 'extend before it lapses — same version, nothing emailed', 'expiring', 'attention-expiring')}
          {section('Price lapsed — still open', `expired inside the open horizon${nudgeDays.openHorizon ? ` (${nudgeDays.openHorizon} days from send)` : ''} and undecided — chase, extend & reinstate, or record the decline; the deal stage is untouched by expiry`, 'expired', 'attention-expired')}
        </>
      )}
    </>
  )
}
